/**
 * Global TypeError handler
 * Catches and handles common TypeError patterns across the application
 */

// Track handled errors to avoid duplicate logging
const handledErrors = new Set();

/**
 * Check if localStorage is available and working
 * @returns {boolean} - True if localStorage works
 */
const isLocalStorageAvailable = () => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false;
        }
        // Test if we can actually use localStorage
        const testKey = '__localStorage_test__';
        window.localStorage.setItem(testKey, 'test');
        window.localStorage.removeItem(testKey);
        return true;
    } catch (error) {
        console.warn('localStorage is not available:', error.message);
        return false;
    }
};

/**
 * Check if matchMedia is available
 * @returns {boolean} - True if matchMedia is supported
 */
const hasMatchMedia = () => {
    try {
        return typeof window !== 'undefined' && 
               typeof window.matchMedia !== 'undefined' &&
               typeof window.matchMedia === 'function';
    } catch (error) {
        return false;
    }
};

/**
 * Categorizes TypeError based on message pattern
 * @param {string} message - Error message
 * @returns {string} - Category name
 */
export const categorizeTypeError = (message) => {
    if (!message) return 'unknown';
    
    const lowerMessage = message.toLowerCase();
    
    // localStorage errors
    if (lowerMessage.includes('localstorage') || 
        lowerMessage.includes('storage') ||
        lowerMessage.includes('getitem') ||
        lowerMessage.includes('setitem')) {
        return 'localStorage';
    }
    
    // DOM errors
    if (lowerMessage.includes('queryselector') ||
        lowerMessage.includes('getelementby') ||
        lowerMessage.includes('undefined is not an object') ||
        lowerMessage.includes('cannot read property') ||
        lowerMessage.includes('null is not an object')) {
        return 'dom';
    }
    
    // Data access errors
    if (lowerMessage.includes('resume.') ||
        lowerMessage.includes('enabled_tabs') ||
        lowerMessage.includes('interests') ||
        lowerMessage.includes('undefined is not an object')) {
        return 'dataAccess';
    }
    
    // Browser-specific errors
    if (lowerMessage.includes('webkit') ||
        lowerMessage.includes('messagehandlers') ||
        lowerMessage.includes('matchmedia')) {
        return 'browserSpecific';
    }
    
    // Draft.js errors
    if (lowerMessage.includes('draft') ||
        lowerMessage.includes('editorstate') ||
        lowerMessage.includes('contentstate')) {
        return 'draftjs';
    }
    
    return 'other';
};

/**
 * Gets helpful suggestion based on error category
 * @param {string} category - Error category
 * @param {string} message - Original error message
 * @returns {string} - Suggestion text
 */
export const getSuggestion = (category, message) => {
    switch (category) {
        case 'localStorage':
            return 'Wrap localStorage calls in try-catch or check availability first';
        
        case 'dom':
            return 'Add null checks: const el = document.querySelector(selector); if (el) { ... }';
        
        case 'dataAccess':
            return 'Use optional chaining: obj?.property?.nested ?? defaultValue';
        
        case 'browserSpecific':
            return 'Check feature availability: if (window.webkit?.messageHandlers) { ... }';
        
        case 'draftjs':
            return 'Error already handled by setupDraftNullthrowsMonitoring()';
        
        default:
            return 'Add null/undefined checks before accessing properties';
    }
};

/**
 * Determines if error should be sent to Sentry
 * @param {Error} error - The error
 * @param {string} category - Error category
 * @returns {boolean} - True if should send to Sentry
 */
export const shouldSendToSentry = (error, category) => {
    const message = error.message || '';
    
    // Only suppress Draft.js errors that are ALREADY handled by draftUtils.js
    if (category === 'draftjs') {
        // Check if this specific error is already caught by draftUtils.js
        const alreadyHandledPatterns = [
            'Got unexpected null or undefined',
            'Minified exception occurred'
        ];
        
        const isAlreadyHandled = alreadyHandledPatterns.some(pattern => 
            message.includes(pattern)
        );
        
        if (isAlreadyHandled) {
            return false; // Already sent by draftUtils.js as 'warning'
        }
        
        // Send other Draft.js errors - they're NOT handled elsewhere
        return true;
    }
    
    // Only filter KNOWN noise patterns from automated testing
    const userAgent = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
    if (userAgent.includes('ghostinspector') || 
        userAgent.includes('headless') ||
        userAgent.includes('selenium')) {
        
        // Only suppress known test noise patterns
        const testNoisePatterns = [
            'QuotaExceededError',
            'exceeded the quota',
            'localStorage quota',
            'NS_ERROR_FILE_CORRUPTED', // Firefox test noise
            'ResizeObserver loop limit exceeded', // Chromium test noise
            'ResizeObserver loop completed' // Another variant
        ];
        
        const isTestNoise = testNoisePatterns.some(pattern => 
            message.toLowerCase().includes(pattern.toLowerCase())
        );
        
        if (isTestNoise) {
            return false; // Filter known test noise only
        }
        
        // Send real errors even from automated tests
        // (Might catch bugs before they affect users!)
        return true;
    }
    
    return true;
};

/**
 * Sets up global TypeError monitoring
 */
export const setupGlobalTypeErrorHandler = () => {
    // Handle synchronous errors
    window.addEventListener('error', (event) => {
        if (!event.error || event.error.name !== 'TypeError') {
            return;
        }
        
        const error = event.error;
        const errorKey = `${error.message}_${error.stack?.substring(0, 100)}`;
        
        // Avoid duplicate handling
        if (handledErrors.has(errorKey)) {
            return;
        }
        handledErrors.add(errorKey);
        
        const category = categorizeTypeError(error.message);
        const suggestion = getSuggestion(category, error.message);
        
        console.group('🔧 TypeError Detected');
        console.error('Message:', error.message);
        console.log('Category:', category);
        console.log('Suggestion:', suggestion);
        console.log('Stack:', error.stack);
        console.groupEnd();
        
        // Send to Sentry with context
        if (window.Sentry && shouldSendToSentry(error, category)) {
            window.Sentry.captureException(error, {
                tags: {
                    errorType: 'TYPE_ERROR',
                    category: category,
                    handled: 'true'
                },
                contexts: {
                    suggestion: {
                        text: suggestion,
                        category: category
                    }
                },
                level: 'error'
            });
        }
    }, true);
    
    // Handle unhandled promise rejections that are TypeErrors
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        
        if (!reason || !(reason instanceof TypeError)) {
            return;
        }
        
        const errorKey = `${reason.message}_${reason.stack?.substring(0, 100)}`;
        
        // Avoid duplicate handling
        if (handledErrors.has(errorKey)) {
            return;
        }
        handledErrors.add(errorKey);
        
        const category = categorizeTypeError(reason.message);
        const suggestion = getSuggestion(category, reason.message);
        
        console.group('🔧 Unhandled TypeError Promise Rejection');
        console.error('Message:', reason.message);
        console.log('Category:', category);
        console.log('Suggestion:', suggestion);
        console.log('Stack:', reason.stack);
        console.groupEnd();
        
        // Send to Sentry with context
        if (window.Sentry && shouldSendToSentry(reason, category)) {
            window.Sentry.captureException(reason, {
                tags: {
                    errorType: 'TYPE_ERROR_PROMISE',
                    category: category,
                    handled: 'true'
                },
                contexts: {
                    suggestion: {
                        text: suggestion,
                        category: category
                    }
                },
                level: 'error'
            });
        }
    });
    
    console.log('✅ Global TypeError handler initialized');
};

/**
 * Polyfills and patches for common issues
 */
export const applyGlobalPatches = () => {
    // Patch localStorage access
    if (!isLocalStorageAvailable()) {
        console.warn('localStorage not available - creating mock');
        
        // Create in-memory storage mock
        const mockStorage = {};
        window.localStorage = {
            getItem: (key) => mockStorage[key] || null,
            setItem: (key, value) => { mockStorage[key] = String(value); },
            removeItem: (key) => { delete mockStorage[key]; },
            clear: () => { Object.keys(mockStorage).forEach(key => delete mockStorage[key]); },
            get length() { return Object.keys(mockStorage).length; },
            key: (index) => Object.keys(mockStorage)[index] || null
        };
    }
    
    // Patch matchMedia if not available
    if (!hasMatchMedia()) {
        console.warn('matchMedia not available - creating polyfill');
        
        window.matchMedia = (query) => ({
            matches: false,
            media: query,
            onchange: null,
            addListener: () => {},
            removeListener: () => {},
            addEventListener: () => {},
            removeEventListener: () => {},
            dispatchEvent: () => true
        });
    }
    
    // Patch webkit.messageHandlers if accessed but not available
    if (typeof window.webkit === 'undefined') {
        try {
            Object.defineProperty(window, 'webkit', {
                get() {
                    console.warn('webkit accessed but not available');
                    return {
                        messageHandlers: {}
                    };
                },
                configurable: true
            });
        } catch (error) {
            // Can't patch in some browsers
            console.warn('Cannot patch webkit:', error);
        }
    }
    
    console.log('✅ Global patches applied');
};

/**
 * Initialize all global error handling
 */
export const initializeGlobalErrorHandling = () => {
    try {
        setupGlobalTypeErrorHandler();
        applyGlobalPatches();
        
        console.log('✅ Global error handling initialized successfully');
    } catch (error) {
        console.error('Error initializing global error handling:', error);
    }
};

export default {
    categorizeTypeError,
    getSuggestion,
    shouldSendToSentry,
    setupGlobalTypeErrorHandler,
    applyGlobalPatches,
    initializeGlobalErrorHandling
};
